import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearPropertyTableDraftRows, getPropertyTableDraftRows } from 'redux/BuilderModeSlice';

import mapBlockMetadataIndicatorsToPropertyTableRows from '../mapBlockMetadataToPropertyTable';

import { buildPropertyTableItemRow, getPropertyTableRows } from './buildPropertyTableGridRows';
import { PROPERTY_TABLE_COLUMNS } from './constants';

import type {
  BlockMetadataIndicator,
  BuilderBlockMetadataResponse,
} from 'types/builderBlockMetadata';

const PROPERTY_TABLE_COLS = PROPERTY_TABLE_COLUMNS as unknown as any[];

/** @returns single changed index, -1 if none, -2 if multiple or length mismatch */
function findSingleChangedMetadataIndex(
  previous: BlockMetadataIndicator[] | null | undefined,
  next: BlockMetadataIndicator[] | undefined,
): number {
  if (!previous || !next || previous.length !== next.length) return -2;
  const changedIndices = next
    .map((indicator, index) => (indicator !== previous[index] ? index : -1))
    .filter((index) => index !== -1);
  if (changedIndices.length === 0) return -1;
  if (changedIndices.length > 1) return -2;
  return changedIndices[0];
}

/**
 * Server rows from metadata + draft rows stored in Redux so `AddIndicator.fulfilled` can
 * append the new indicator and drop the committed draft in one update (no UI flash).
 *
 * Grid `rows` use structural sharing: when Immer leaves other indicators untouched (same
 * object refs) and exactly one row changes, only that row is rebuilt — not the whole table.
 */
export default function usePropertyTableRowsFromMetadata(
  blockMetadata: BuilderBlockMetadataResponse | null,
  metadataIndicators: BlockMetadataIndicator[],
  blockId: string | undefined,
  scenarioId: number | undefined,
) {
  const dispatch = useDispatch();
  const draftRows = useSelector((state: any) => getPropertyTableDraftRows(state));

  const previousIndicatorsReference = useRef<BlockMetadataIndicator[] | null>(null);
  const previousRowsReference = useRef<any[] | null>(null);
  const previousDraftKeyReference = useRef<string>('');
  const previousBlockScenarioKeyReference = useRef<string>('');

  const blockScenarioKey = `${blockId ?? ''}-${scenarioId ?? ''}`;
  if (previousBlockScenarioKeyReference.current !== blockScenarioKey) {
    previousBlockScenarioKeyReference.current = blockScenarioKey;
    previousIndicatorsReference.current = null;
    previousRowsReference.current = null;
    previousDraftKeyReference.current = '';
  }

  useEffect(() => {
    dispatch(clearPropertyTableDraftRows());
  }, [blockId, scenarioId, dispatch]);

  const serverTableData = useMemo(
    () =>
      blockMetadata?.indicators?.length
        ? mapBlockMetadataIndicatorsToPropertyTableRows(blockMetadata.indicators)
        : [],
    [blockMetadata?.indicators],
  );

  const draftKey = useMemo(
    () => draftRows.map((row: { id?: string | number }) => String(row.id)).join('|'),
    [draftRows],
  );

  const tableData = useMemo(() => [...serverTableData, ...draftRows], [serverTableData, draftRows]);

  const rows = useMemo(() => {
    const previousIndicators = previousIndicatorsReference.current;
    const previousRows = previousRowsReference.current;
    const previousDraftKey = previousDraftKeyReference.current;

    const changedIndex = findSingleChangedMetadataIndex(previousIndicators, metadataIndicators);
    const draftsUnchanged = draftKey === previousDraftKey;
    const rowCountMatches =
      previousRows !== null &&
      metadataIndicators !== undefined &&
      previousRows.length === tableData.length + 2;

    if (
      changedIndex >= 0 &&
      draftsUnchanged &&
      rowCountMatches &&
      previousIndicators &&
      previousIndicators[changedIndex] &&
      metadataIndicators[changedIndex] &&
      previousIndicators[changedIndex].name === metadataIndicators[changedIndex].name
    ) {
      const nextRows = previousRows!.slice();
      const item = mapBlockMetadataIndicatorsToPropertyTableRows([
        metadataIndicators[changedIndex],
      ])[0];
      nextRows[1 + changedIndex] = buildPropertyTableItemRow(
        item,
        PROPERTY_TABLE_COLS,
        metadataIndicators,
        tableData.length,
        changedIndex,
      );
      previousIndicatorsReference.current = metadataIndicators;
      previousRowsReference.current = nextRows;
      previousDraftKeyReference.current = draftKey;
      return nextRows;
    }

    if (changedIndex === -1 && draftsUnchanged && rowCountMatches && previousRows) {
      previousIndicatorsReference.current = metadataIndicators;
      previousDraftKeyReference.current = draftKey;
      return previousRows;
    }

    const fullRows = getPropertyTableRows(tableData, PROPERTY_TABLE_COLS, metadataIndicators);
    previousIndicatorsReference.current = metadataIndicators;
    previousRowsReference.current = fullRows;
    previousDraftKeyReference.current = draftKey;
    return fullRows;
  }, [tableData, metadataIndicators, draftKey]);

  return { tableData, rows };
}
